/**
 * Waiver hooks (member-facing).
 *
 * @module services/internalApi/waiver/hooks
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { useAuth } from '~/services/auth';
import { getActiveWaivers, checkWaiverStatus } from '../api/waiverService';
import type { ActiveWaiver, WaiverStanding } from '../types';

/**
 * Module-scoped so the `?? …` fallbacks are referentially stable too.
 *
 * A fresh `[]` per render is not a style wart. `required` below feeds a
 * route-level gate and, historically, a guarded derive-during-render in the
 * checkout gate — and a guard that compares an array reallocated every render
 * can never converge. That is React error #301, a blank screen, and a lost
 * checkout, not a perf nit. Sentry 7712847551.
 */
const NO_ACTIVE_WAIVERS: ActiveWaiver[] = [];
const NO_STANDING: WaiverStanding[] = [];

export interface UseActiveWaiversOptions {
  clubId: number | null | undefined;
  enabled?: boolean;
}

/**
 * The waivers this club asks members to sign.
 *
 * Returns `[]` rather than null when a club has none, so callers can render
 * nothing without a separate "configured?" check.
 */
export function useActiveWaiversQuery({ clubId, enabled = true }: UseActiveWaiversOptions) {
  const clubIdNumber = clubId ? Number(clubId) : null;

  const query = useQuery<ActiveWaiver[], Error>({
    queryKey: clubIdNumber ? queryKeys.waiver.active(clubIdNumber) : queryKeys.disabled.waiver(),
    queryFn: () => {
      if (!clubIdNumber) throw new Error('Club ID is required');
      return getActiveWaivers(clubIdNumber);
    },
    enabled: enabled && !!clubIdNumber,
    ...cachePresets.staticData, // Waivers rarely change
  });

  return {
    ...query,
    waivers: query.data ?? NO_ACTIVE_WAIVERS,
  };
}

/**
 * Which waivers this member still owes. Authenticated only.
 *
 * Not `staticData` caching: a club can publish a waiver at any moment, and a
 * 15-minute cache would let a member walk past a brand-new one. `frequent`
 * refetches on focus, which is when the gate matters.
 */
export function useWaiverStandingQuery({ clubId, enabled = true }: UseActiveWaiversOptions) {
  const clubIdNumber = clubId ? Number(clubId) : null;

  const query = useQuery<WaiverStanding[], Error>({
    queryKey: clubIdNumber
      ? queryKeys.waiver.standing(clubIdNumber)
      : queryKeys.disabled.waiver(),
    queryFn: () => {
      if (!clubIdNumber) throw new Error('Club ID is required');
      return checkWaiverStatus(clubIdNumber);
    },
    enabled: enabled && !!clubIdNumber,
    ...cachePresets.frequent,
  });

  const standing = query.data ?? NO_STANDING;

  // Memoized because `pending` escapes this hook: it drives the route-level
  // `WaiverGate` and becomes `required` below. `query.data` is referentially
  // stable across re-renders, so this recomputes only when the server's answer
  // actually changes.
  const pending = useMemo(() => standing.filter((w) => !w.signed), [standing]);

  return {
    ...query,
    standing,
    pending,
  };
}

/**
 * The waivers this visitor still has to sign — the single question both the
 * entry gate and the checkout section ask.
 *
 * The answer depends on whether there is a session, and that is the whole
 * reason this hook exists:
 *
 * - **Authenticated** → standing, filtered to unsigned. Someone who cleared the
 *   entry gate must not be asked again at checkout, and `getActiveWaivers`
 *   cannot tell us that because it reports every active waiver regardless of
 *   who is asking.
 * - **Anonymous** → every active waiver. A guest has no signature history to
 *   consult, and `checkWaiverStatus` would 401.
 */
export function useWaiverRequirements(clubId: number | null | undefined) {
  const { isAuthenticated } = useAuth();

  const active = useActiveWaiversQuery({ clubId, enabled: !isAuthenticated });
  const standing = useWaiverStandingQuery({ clubId, enabled: isAuthenticated });

  const source = isAuthenticated ? standing : active;

  return {
    /** Waivers still requiring a signature from this visitor. */
    required: isAuthenticated ? standing.pending : active.waivers,
    /**
     * An answer has ARRIVED at some point — not that the query is currently
     * settled. Callers gating on this must not substitute `!isLoading` (a FAILED
     * query is also "not loading", and `required` is `[]` there, which every
     * completeness check reads as vacuously satisfied) NOR `isSuccess` (which
     * goes false on a background refetch failure while the good data is still
     * cached — that blocks bookings at clubs which have no waiver at all).
     *
     * Absence of an answer is not permission. A stale answer is still an answer.
     */
    hasAnswer: source.data !== undefined,
    /**
     * A first answer is still in flight.
     *
     * For MESSAGING only, never for the gate: `hasAnswer` above is the whole
     * safety decision. This exists so a caller can tell "no answer yet" from
     * "no answer coming" and avoid rendering an ordinary load as a failure.
     *
     * No `isError` here on purpose — it is the flag people reach for, and it is
     * wrong twice over: it misses the paused (offline) case entirely, and it
     * invites gating on query status, which is what this hook's `hasAnswer`
     * exists to stop.
     */
    isLoading: source.isLoading,
    refetch: source.refetch,
  };
}
